import React, { useContext, useEffect, useState } from 'react';
import { UseFormMethods } from 'react-hook-form';
import Card from 'src/components/common/Card.redesign';
import { ContextMenuAction } from 'src/components/common/ContextMenu';
import { FormRow } from 'src/components/controls/forms/reactHookForm';
import RHFCheckbox from 'src/components/controls/tempRHFControls/RHFCheckBox';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import OwnershipContactCategory from 'src/models/OwnershipContactCategory';
import Ownership from 'src/models/tract_detail/Ownership';
import RHFTextArea from 'src/components/controls/tempRHFControls/RHFTextArea';
import { LandSystem } from 'src/models/LandSystem';
import Horizon from 'src/models/tract_detail/Horizon';
import { Formation, HorizonInterval } from 'src/models/Horizon';
import { DialogContext, dialogCreators } from 'src/providers/DialogProvider';
import { IAppContextState } from 'src/reducers/AppContext';
import { AppContext } from 'src/providers/AppContextProvider';
import { useFetch } from 'use-http';
import AddOwner from 'src/models/tract_detail/add_ownership_revised/AddOwner';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import AddOwnershipData from 'src/models/tract_detail/add_ownership_revised/AddOwnership';
import TractDetail from 'src/models/tract_detail/TractDetail';
import GlobalOwnershipList from '../../TractComponents/OwnershipList.redesign';
import AnimateHeight from 'react-animate-height';
import HorizonSection, { IHorizonSectionProps } from './HorizonSection';
import CloneHorizon from 'src/models/CloneHorizon';
import Township from 'src/models/Township';
import Section from 'src/models/Section';
import TractDetailData from 'src/models/tract_detail/TractDetail';
import _ from 'lodash';
import CreateHorizonDto from 'src/models/tract_detail/CreateHorizonDto';
import TextHelper from 'src/util/Text';
import { SelectItem } from 'src/models/common/SelectItem';

interface IMineralTractCardProps extends Partial<Pick<UseFormMethods, 'register' | 'errors' | 'getValues' | 'formState' | 'reset'>> {
    index: number;
    tractDetail: TractDetail;
    landSystem: LandSystem;
    mineralTractAcreage: number;
    contactCategories: Array<OwnershipContactCategory>;
    leaseBuyerList: Array<SelectItem>;
    horizons: Array<Horizon>;
    formations: Array<Formation>;
    loadSurfaceTract: () => void;
    openContactNotes: (ownership: Ownership) => void;
    onAddFormation: (formation: Formation) => void;
    sequenceHorizons: (sequence: Array<{ id: number; sequence: number }>) => void;
    updateCurrentTract: (tractId: number, horizonNumber: number) => void;
    showDialog: (dialogName: string) => void;
    onOpenHorizonDialog: (dialogParams: IHorizonSectionProps) => void;
}

const MineralTractCard = (props: IMineralTractCardProps) => {
    const appContext = useContext<IAppContextState>(AppContext);
    const dialogContext = useContext<dialogCreators>(DialogContext);
    const { get, post, del, response } = useFetch();
    const dispatch = useDispatch();
    const [isPartial, setIsPartial] = useState<boolean>(false);
    const [showOwners, setShowOwners] = useState<boolean>(false);
    // Horizon Data
    const [newCloneHorizon, setNewCloneHorizon] = useState<CloneHorizon>(undefined);
    const [cloneTownships, setCloneTownships] = useState<Array<Township>>([]);
    const [cloneSections, setCloneSections] = useState<Array<Section>>([]);
    const [cloneTracts, setCloneTracts] = useState<Array<TractDetailData>>([]);
    const [cloneHorizons, setCloneHorizons] = useState<Array<HorizonInterval>>([]);
    const [uniqueHorizonIntervals, setUniqueHorizonIntervals] = useState<Array<HorizonInterval>>([]);
    const [idVerificationMessage, setIdVerificationMessage] = useState<string>('');
    const [idVerificationResult, setIdVerificationResult] = useState<boolean>(undefined);
    const [currentHorizonNumber, setCurrentHorizonNumber] = useState<number>(1);
    const [currentHorizon, setCurrentHorizon] = useState<number>(0);

    const namePrefix = 'mineralTracts';
    const menuActions: ContextMenuAction[] = [
        { text: 'Add Ownership', action: () => addOwnership() },
        { text: 'Delete Mineral Tract', action: () => deleteMineralTract() },
    ];

    useEffect(() => {
        cloneHorizonData();
        setShowOwners(false);
        getUniqueHorizonIntervals(props.tractDetail?.tractNumber);
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);
        if (!!props.tractDetail) {
            getUniqueHorizonIntervals(props.tractDetail?.tractNumber);
        }
        if (props.tractDetail?.horizons[currentHorizon]?.mineralOwnerships?.length > 0) {
            setShowOwners(true);
        } else {
            setShowOwners(false);
        }
        const index = _.findIndex(
            props.tractDetail?.horizons?.map((x) => {
                return { horizon: x, horizonNumber: x.horizonInterval?.horizonNumber };
            }),
            { horizonNumber: currentHorizonNumber }
        );
        if (index >= 0) {
            setCurrentHorizon(index);
        }
    }, [props.tractDetail]);

    const tractLabelDescription = () => {
        return props.landSystem === LandSystem.Eastern ? 'Parcel Id' : 'Tract Name';
    };

    const onUpdatePartial = () => {
        let ref = props.getValues();
        if (ref.mineralTracts[props.index]) {
            setIsPartial(ref.mineralTracts[props.index].horizons[0]?.isPartial);
        }
    };

    const getCloneTracts = async () => {
        const result = await get(
            `v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/fields/${appContext.sectionNumber}/tracts/tract-details`
        );

        if (response.ok) {
            // CHECK THIS
            console.log('Clone Tracts:', result);
            setCloneTracts(result);
        }
    };

    const getCloneTownships = async () => {
        const result = await get(`v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/townships`);

        if (response.ok) {
            // CHECK THIS
            console.log('Clone Townships:', result);
            setCloneTownships(result);
        }
    };

    const getCloneSections = async (townshipId: number) => {
        const result = await get(`v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/townships/${townshipId}/sections`);

        if (response.ok) {
            // CHECK THIS
            console.log('Clone Sections:', result);
            setCloneSections(result);
        }
    };

    const navigate = (path: string, search?: string) => {
        dispatch(push({ pathname: path, search }));
    };

    const getUniqueHorizonIntervals = async (tractNumber: number) => {
        const result = await get(`v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/formations`);

        if (response.ok) {
            // CHECK THIS
            console.log('Unique Horizon Intervals:', result);
            setUniqueHorizonIntervals(result);
        }
    };

    const verifyId = async (context: string, id: string) => {
        const params = `?context=${context}&id=${id}`;
        const result = await get(`v1.0/search/verify-id${params}`);

        if (response.ok) {
            setIdVerificationMessage(result.result ? `${TextHelper.toProperCase(context)} found` : `${TextHelper.toProperCase(context)} not found`);
            setIdVerificationResult(result.result);
        }
    };

    const setVerificationMessage = (message: string) => {
        setIdVerificationMessage(message);
        setIdVerificationResult(false);
    };

    const onCreateHorizon = async (horizon: CreateHorizonDto) => {
        horizon.currentHorizonId = props.tractDetail.horizons[currentHorizon].id;
        await post(
            `v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/fields/${appContext.sectionNumber}/tracts/${props.tractDetail.tractNumber}/create-horizon`,
            horizon
        );

        if (response.ok) {
            props.loadSurfaceTract();
        }
    };

    const addDeviation = async (deviationOwner: AddOwner) => {
        const result = await post(
            `v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/owners/add-deviation`,
            deviationOwner
        );

        if (response.ok) {
            console.log(result);
        }
    };

    const onNavigate = (path: string) => {
        dispatch(push(path));
    };

    const onSaveAddOwnership = async (owner: AddOwnershipData) => {
        await post(
            `v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/fields/${appContext.sectionNumber}/tracts/${appContext.surfaceTractNumber}/save-add-ownership`,
            owner
        );

        if (response.ok) {
            props.loadSurfaceTract();
        }
    };

    const addOwnership = () => {
        dialogContext.tract.createMineralOwnership(
            'Add Ownership',
            'ADD_OWNERSHIP',
            addDeviation,
            onNavigate,
            null,
            onSaveAddOwnership,
            props.tractDetail,
            currentHorizon,
            0
        );
    };

    const cloneHorizonData = () => {
        if (props.tractDetail?.horizons) {
            let data: CloneHorizon;
            if (!newCloneHorizon) {
                data = new CloneHorizon();
                data.townshipId = props.tractDetail?.townshipId;
                data.sectionNumber = appContext.sectionNumber;
                data.tractNumber = props.tractDetail?.tractNumber;
                data.horizonNumber = 0;
            } else {
                data = newCloneHorizon;
            }

            data.updateTownships(cloneTownships);
            data.updateSections(cloneSections);
            data.updateTracts(cloneTracts);
            data.updateHorizons(cloneHorizons);

            setNewCloneHorizon(data);
        }

        setNewCloneHorizon(undefined);
    };

    const deleteMineralTract = async () => {
        let hasOwnerships = false;

        for (let i = 0; i < props.tractDetail?.horizons.length; i++) {
            hasOwnerships = hasOwnerships || props.tractDetail.horizons[i].mineralOwnerships?.length > 0;
            if (hasOwnerships === true) {
                break;
            }
        }

        if (hasOwnerships) {
            window.alert('Please delete ownerships between all horizons before detaching mineral tract');
        } else {
            const confirmAction = async () => {
                // tslint:disable-next-line: max-line-length
                await del(`v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/fields/${appContext.fieldNumber}/surfaceTracts/${appContext.surfaceTractNumber}/detachTract/${props.tractDetail.id}`);
    
                if (response.ok) {
                    props.loadSurfaceTract();
                }
            };
    
            const body = (
                <>
                    <div style={{ fontWeight: 'bold' }}>
                        Are you sure you want to remove Mineral {tractDescription()} {props.tractDetail.id}?
                    </div>
                </>
            );
    
            dialogContext.common.confirmAction(`Delete Mineral ${tractDescription()}`, body, confirmAction);
        }
    };

    const tractDescription = () => {
        return props.landSystem === LandSystem.Eastern ? 'Parcel' : 'Tract';
    };

    const getCloneData = (cloneHorizon: CloneHorizon) => {
        switch (cloneHorizon.currentLevel) {
            case 'Horizon': {
                const t = cloneTracts.filter((tract: TractDetailData) => tract.tractNumber === cloneHorizon.tractNumber)[0];

                const his = t.horizons.map((horizon: Horizon) => {
                    const hi = HorizonInterval.from(horizon.horizonInterval);
                    hi.from.calcType();
                    hi.to.calcType();
                    return hi;
                });

                setCloneHorizons(his);
                break;
            }
            case 'Tract': {
                getCloneTracts();
                break;
            }
            case 'Section': {
                getCloneSections(cloneHorizon.townshipId);
                break;
            }
            case 'Township': {
                getCloneTownships();
                break;
            }
            default: {
                console.log('getCloneData - unexpected case hit');
            }
        }
    };

    const onChangeHorizon = (horizonId: number) => {
        const index = _.findIndex(props.tractDetail.horizons, { id: horizonId });
        const horizonNumber = props.tractDetail.horizons[index].horizonInterval.horizonNumber;

        setCurrentHorizonNumber(horizonNumber);
        setCurrentHorizon(index);
    };

    const renderOwnerships = () => {
        return (
            <GlobalOwnershipList
                tractId={props.tractDetail?.id}
                ownerships={props.tractDetail?.horizons[currentHorizon]?.mineralOwnerships}
                owners={props.tractDetail?.owners}
                tractDetail={props.tractDetail}
                horizonNumber={currentHorizon}
                namePrefix={`${namePrefix}[${props.index}].horizons[${currentHorizon}].mineralOwnerships`}
                ownershipContactCategories={props.contactCategories}
                projectType={'Surface'}
                ownershipType={'Mineral'}
                grossAcres={props.mineralTractAcreage}
                leaseBuyerList={props.leaseBuyerList}
                loadSurfaceTract={() => props.loadSurfaceTract()}
                register={props.register}
                errors={props.errors}
                getValues={props.getValues}
                formState={props.formState}
                reset={props.reset}
                addDeviation={(deviationOwner: AddOwner) => addDeviation(deviationOwner)}
                onNavigate={(path: string) => onNavigate(path)}
                onSaveAddOwnership={(owner: AddOwnershipData) => onSaveAddOwnership(owner)}   
            />
        );
    };

    return (
        <>
            <Card contextActions={menuActions} newContext={true} showMenu={true} badge={(props.tractDetail?.tractNumber).toString()}>
                <div style={{ padding: '1rem', boxSizing: 'border-box' }}>
                <div className="mr-1" hidden={true}>
                        <RHFInputBox
                            label=""
                            name={`${namePrefix}[${props.index}].id`}
                            register={props.register}
                            disabled={true}
                            errors={props.errors}
                        />
                    </div>
                    <div className="mr-1" hidden={true}>
                        <RHFInputBox
                            label=""
                            name={`${namePrefix}[${props.index}].tractNumber`}
                            register={props.register}
                            disabled={true}
                            errors={props.errors}
                        />
                    </div>
                    <div className="mr-1" hidden={true}>
                        <RHFInputBox
                            label=""
                            name={`${namePrefix}[${props.index}]horizons[0].id`}
                            register={props.register}
                            disabled={true}
                            errors={props.errors}
                        />
                    </div>
                    <FormRow>
                        <RHFInputBox
                            label={tractLabelDescription()}
                            name={`${namePrefix}[${props.index}].tractName`}
                            register={props.register}
                            errors={props.errors}
                            style={{ width: '100%' }}
                        />
                        <RHFInputBox
                            label="Acres"
                            name={`${namePrefix}[${props.index}].grossAcres`}
                            type="number"
                            register={props.register}
                            errors={props.errors}
                            style={{ width: '100%' }}
                        />
                        <RHFCheckbox
                            label="Partial"
                            name={`${namePrefix}[${props.index}].horizons[0].isPartial`}
                            register={props.register}
                            errors={props.errors}
                            onClick={() => onUpdatePartial()}
                        />
                        {isPartial && (
                            <RHFInputBox
                                label="Partial Acreage"
                                name={`${namePrefix}[${props.index}].horizons[0].partialAcreage`}
                                type="number"
                                register={props.register}
                                errors={props.errors}
                                style={{ width: '100%' }}
                            />
                        )}
                    </FormRow>
                    <FormRow>
                        <RHFInputBox
                            label="Grantor"
                            name={`${namePrefix}[${props.index}].horizons[0].grantor`}
                            bodyStyle={{ width: '50%' }}
                            style={{ width: '100%' }}
                            register={props.register}
                            errors={props.errors}
                        />
                        <RHFInputBox
                            label="Grantee"
                            name={`${namePrefix}[${props.index}].horizons[0].grantee`}
                            bodyStyle={{ width: '50%' }}
                            style={{ width: '100%' }}
                            register={props.register}
                            errors={props.errors}
                        />
                    </FormRow>
                    <FormRow>
                        <RHFInputBox
                            label="Instrument Number"
                            name={`${namePrefix}[${props.index}].horizons[0].sourceReference`}
                            register={props.register}
                            errors={props.errors}
                            style={{ width: '100%' }}
                        />
                        <RHFInputBox
                            label="Date"
                            name={`${namePrefix}[${props.index}].horizons[0].sourceDate`}
                            type="date"
                            register={props.register}
                            errors={props.errors}
                            style={{ width: '100%' }}
                        />
                        <div style={{ width: '25%' }} />
                    </FormRow>
                    <FormRow className="row">
                        <RHFTextArea
                            name={`${namePrefix}[${props.index}].legalDescription`}
                            label="Legal Description"
                            rows={5}
                            register={props.register}
                            errors={props.errors}
                        />
                    </FormRow>
                    {props.horizons?.length > 0 && (
                        <div>
                            <HorizonSection
                                selectedHorizon={props.horizons[currentHorizon]}
                                horizons={props.horizons}
                                horizonNumber={props.horizons[currentHorizon]?.horizonInterval.horizonNumber}
                                formations={props.formations}
                                cloneHorizonData={newCloneHorizon}
                                onGetData={(val) => getCloneData(val)}
                                uniqueHorizonIntervals={uniqueHorizonIntervals}
                                onChangeHorizon={onChangeHorizon}
                                showDialog={props.showDialog}
                                currentTract={props.tractDetail}
                                currentClientNumber={appContext.clientNumber}
                                currentProjectNumber={appContext.projectNumber}
                                isFromChild={props.tractDetail.isChild}
                                sequenceHorizons={props.sequenceHorizons}
                                onCreateHorizon={onCreateHorizon}
                                onCreateFormation={props.onAddFormation}
                                initializeOwnershipList={() => console.log('updating current horizon for Tract:', props.tractDetail.tractNumber)}
                                idVerificationMessage={idVerificationMessage}
                                idVerificationResult={idVerificationResult}
                                setVerificationMessage={setVerificationMessage}
                                verifyId={verifyId}
                                navigate={navigate}
                                updateTractAndHorizons={props.updateCurrentTract}
                                onOpenHorizonDialog={(dialogParams: IHorizonSectionProps) => props.onOpenHorizonDialog(dialogParams)}
                            />
                        </div>
                    )}
                    <AnimateHeight duration={300} height={showOwners ? 'auto' : 0}>
                        <div className="ownership-breakdown">{renderOwnerships()}</div>
                    </AnimateHeight>
                    <div style={{ clear: 'both' }} />
                </div>
            </Card>
        </>
    );
};

export default MineralTractCard;



// WEBPACK FOOTER //
// ./src/components/project/sections/tract/components/MineralTractCard.tsx